import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Basic inline styles for the application (unchanged)
const styles = {
    appContainer: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    header: {
        textAlign: 'center',
        color: '#333',
    },
    section: {
        padding: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        marginBottom: '20px',
        backgroundColor: '#f9f9f9',
    },
    uploaderContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
    },
    input: {
        padding: '8px',
        border: '1px solid #ccc',
        borderRadius: '4px',
    },
    button: {
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        padding: '10px 15px',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'background-color 0.3s ease',
    },
    buttonHover: {
        backgroundColor: '#0056b3',
    },
    searchBar: {
        padding: '8px 12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        width: '250px',
    },
    filterButtons: {
        display: 'flex',
        gap: '8px',
    },
    filterButton: {
        backgroundColor: '#e2e6ea',
        color: '#333',
    },
    filterButtonActive: {
        backgroundColor: '#007bff',
        color: 'white',
    },
    listContainer: {
        marginTop: '20px',
    },
    documentList: {
        listStyle: 'none',
        padding: '0',
    },
    documentItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px',
        borderBottom: '1px solid #eee',
    },
    documentName: {
        fontWeight: 'bold',
        flexGrow: 1,
    },
    actionButton: {
        backgroundColor: '#28a745',
        color: 'white',
        border: 'none',
        padding: '8px 12px',
        borderRadius: '4px',
        cursor: 'pointer',
        marginLeft: '10px',
        transition: 'background-color 0.3s ease',
    },
    noDocuments: {
        textAlign: 'center',
        color: '#000000ff',
        marginTop: '20px',
        fontStyle: 'italic',
    },
};


const MyDocuments = () => {
    const [documents, setDocuments] = useState([]);
    const [filteredDocuments, setFilteredDocuments] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');

    const [searchTerm, setSearchTerm] = useState(
        () => sessionStorage.getItem('documentSearchTerm') || ''
    );
    const [selectedType, setSelectedType] = useState(
        () => sessionStorage.getItem('documentSelectedType') || 'All'
    );
    // Fetch projects for dropdown
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/Projects`, {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true,
                });
                setProjects(res.data);
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
        };
        fetchProjects();
    }, []);

    const fetchDocuments = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projectdocument`);
            // Filter out any documents with a file size of 0 bytes
            const validDocuments = response.data.filter(doc => doc.fileSize > 0);
            setDocuments(validDocuments);
            setFilteredDocuments(validDocuments);
        } catch (error) {
            console.error('Error fetching documents:', error);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, []);

    useEffect(() => {
        let filtered = documents;

        if (selectedType !== 'All') {
            filtered = filtered.filter(doc => doc.fileType.includes(selectedType.toLowerCase()));
        }

        if (searchTerm) {
            filtered = filtered.filter(doc =>
                doc.originalName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredDocuments(filtered);

        sessionStorage.setItem('documentSearchTerm', searchTerm);
        sessionStorage.setItem('documentSelectedType', selectedType);

    }, [documents, searchTerm, selectedType]);

    const onFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };

    const onFileUpload = async () => {
        if (!selectedFile) {
            alert('Please select a file first!');
            return;
        }
        if (!selectedProject) {
            alert('Please select a project!');
            return;
        }

        const formData = new FormData();
        formData.append('document', selectedFile);
        formData.append('projectId', selectedProject);

        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/projectdocument/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            alert('File uploaded successfully!');
            setSelectedFile(null);
            setSelectedProject('');
            fetchDocuments();
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('File upload failed!');
        }
    };


    const handleDelete = async (filename) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/projectdocument/delete/${filename}`);
            alert('Document deleted successfully!');
            fetchDocuments();
        } catch (error) {
            console.error('Error deleting document:', error);
            alert('Failed to delete document.');
        }
    };

    const handleDownload = (filename) => {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/projectdocument/download/${filename}`);
    };

    const isViewable = (fileType, filename) => {
        const fileExtension = filename.split('.').pop().toLowerCase();
        const browserViewableTypes = ['image', 'application/pdf', 'text/plain'];
        if (browserViewableTypes.some(type => fileType.includes(type))) {
            return { viewer: 'browser', url: `${process.env.REACT_APP_BACKEND_URL}/api/documents/view/${filename}` };
        }
        const googleViewableExtensions = ['docx', 'xlsx', 'ppt', 'pptx'];
        if (googleViewableExtensions.includes(fileExtension)) {
            const fileUrl = encodeURIComponent(`${process.env.REACT_APP_BACKEND_URL}/api/projectdocument/view/${filename}`);
            return { viewer: 'google', url: `https://docs.google.com/gview?url=${fileUrl}&embedded=true` };
        }
        return { viewer: 'unsupported' };
    };

    const handleView = (fileType, filename) => {
        const viewerInfo = isViewable(fileType, filename);
        if (viewerInfo.viewer !== 'unsupported') {
            window.open(viewerInfo.url, '_blank');
        } else {
            alert('This file type cannot be viewed in the browser.');
        }
    };

    const getFileTypes = () => {
        const types = new Set(documents.map(doc => {
            // Note: This function now works because documents with undefined properties are filtered out
            const fileExtension = doc.fileType.split('/').pop();
            if (fileExtension.includes('spreadsheetml')) {
                return 'xlsx';
            }
            if (fileExtension.includes('document')) {
                return 'docx';
            }
            if (fileExtension.includes('pdf')) {
                return 'pdf';
            }
            if (fileExtension.includes('plain')) {
                return 'txt';
            }
            return fileExtension;
        }));
        return ['All', ...Array.from(types)];
    };
    
    const formatFileSize = (sizeInBytes) => {
        if (sizeInBytes < 1024) {
            return `${sizeInBytes} B`;
        } else if (sizeInBytes < 1024 * 1024) {
            return `${(sizeInBytes / 1024).toFixed(2)} KB`;
        } else {
            return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
        }
    };

    return (
        <div style={styles.appContainer}>
            <h1 style={styles.header}>Document Management System</h1>

            {/* File Uploader Section */}
            <div style={styles.section}>
                <h2 style={styles.header}>Upload a Document</h2>
                <div style={styles.uploaderContainer}>
                    <select
                        value={selectedProject}
                        onChange={e => setSelectedProject(e.target.value)}
                        style={styles.input}
                    >
                        <option value="">Select Project</option>
                        {projects.map(project => (
                            <option key={project._id} value={project._id}>
                                {project.caseTitle || project.caseNumber}
                            </option>
                        ))}
                    </select>
                    <input type="file" onChange={onFileChange} style={styles.input} />
                    <button onClick={onFileUpload} style={styles.button}>
                        Upload
                    </button>
                    {selectedFile && (
                        <span>
                            Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                    )}
                </div>
            </div>

            {/* Search and Filter Section */}
            <div style={styles.section}>
                <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '4px' }}
                >
                    {getFileTypes().map(type => (
                        <option key={type} value={type}>
                            {type.toUpperCase()}
                        </option>
                    ))}
                </select>

                <div style={{ marginTop: '10px', position: 'relative' }}>
                    <input
                        type="text"
                        placeholder="Search by document name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ ...styles.searchBar, paddingRight: '30px' }}
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')} // Corrected: Removed extra comma
                            style={{
                                position: 'absolute',
                                right: '15px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                color: '#666',
                            }}
                        >
                            <span>&times;</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Document List Section */}
            <div style={styles.listContainer}>
                <h2 style={styles.header}>Uploaded Documents</h2>
                <ul style={styles.documentList}>
                    {filteredDocuments.length > 0 ? (
                        filteredDocuments.map((doc) => (
                            <li key={doc._id} style={styles.documentItem}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={styles.documentName}>
                                        {doc.originalName}
                                    </span>
                                    <span style={{ fontSize: '12px', color: '#666' }}>
                                        Uploaded on: {new Date(doc.uploadDate).toLocaleDateString()} | Size: {formatFileSize(doc.fileSize)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    {isViewable(doc.fileType, doc.originalName).viewer !== 'unsupported' && (
                                        <button onClick={() => handleView(doc.fileType, doc.originalName)} style={styles.actionButton}>View</button>
                                    )}
                                    <button onClick={() => handleDownload(doc.filename)} style={styles.actionButton}>Download</button>
                                    <button onClick={() => handleDelete(doc.filename)} style={{ ...styles.actionButton, backgroundColor: '#dc3545' }}>Delete</button>
                                </div>
                            </li>
                        ))
                    ) : (
                        <div style={styles.noDocuments}>
                            No Results Found.
                        </div>
                    )}
                </ul>
            </div>
        </div>
    );
};

export default MyDocuments;